/**
 * Encodage WAV 16-bit mono.
 *
 * Le backend attend toujours du WAV (`dsp.load_wav`) et travaille en mono :
 * on encode donc côté client, ce qui règle d'un coup le problème des codecs
 * d'enregistrement (WebM/Opus sur Chrome, MP4/AAC sur Safari) — on ne transmet
 * jamais le format natif du navigateur, seulement du PCM.
 */

import { track } from '../lib/track';

/** Mixdown mono d'un AudioBuffer (moyenne des canaux). */
export function toMono(buffer: AudioBuffer, from = 0, to?: number): Float32Array {
  const sr = buffer.sampleRate;
  const a = Math.max(0, Math.floor(from * sr));
  const b = Math.min(buffer.length, Math.floor((to ?? buffer.duration) * sr));
  const len = Math.max(0, b - a);
  const out = new Float32Array(len);
  const chans = buffer.numberOfChannels;

  for (let c = 0; c < chans; c++) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < len; i++) out[i] += data[a + i];
  }
  if (chans > 1) for (let i = 0; i < len; i++) out[i] /= chans;
  return out;
}

export function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const bytes = 44 + samples.length * 2;
  const view = new DataView(new ArrayBuffer(bytes));

  const str = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
  };

  str(0, 'RIFF');
  view.setUint32(4, bytes - 8, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits
  str(36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let off = 44;
  for (let i = 0; i < samples.length; i++, off += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(off, Math.round(s * 32767), true);
  }
  return new Blob([view.buffer], { type: 'audio/wav' });
}

/** Raccourci : AudioBuffer (ou tranche) → Blob WAV prêt pour l'API. */
export function bufferToWav(buffer: AudioBuffer, from = 0, to?: number): Blob {
  return encodeWav(toMono(buffer, from, to), buffer.sampleRate);
}

/**
 * Mixe plusieurs pistes (mono, avec volume) en un seul WAV.
 * Longueur = la piste la plus longue ; anti-clip par normalisation si le mix dépasse 1.
 */
export function mixTracksToWav(
  items: { buffer: AudioBuffer; volume: number }[],
  sampleRate: number,
): Blob {
  let len = 0;
  for (const it of items) if (it.buffer.length > len) len = it.buffer.length;
  const mix = new Float32Array(len);
  for (const it of items) {
    const mono = toMono(it.buffer);
    const v = Math.max(0, Math.min(1, it.volume));
    for (let i = 0; i < mono.length; i++) mix[i] += mono[i] * v;
  }
  let peak = 0;
  for (let i = 0; i < len; i++) {
    const a = Math.abs(mix[i]);
    if (a > peak) peak = a;
  }
  if (peak > 1) for (let i = 0; i < len; i++) mix[i] /= peak;
  return encodeWav(mix, sampleRate);
}

export function download(blob: Blob, filename: string): void {
  track('export-audio', { format: filename.split('.').pop() ?? 'wav' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // iOS Safari ne télécharge le blob qu'APRÈS la confirmation utilisateur :
  // révoquer trop tôt casse le téléchargement. On laisse 60 s.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
